import React, { useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { CellStyle, ColDef } from "ag-grid-community";
import { formatNumber } from "../../../../services/mixin";
import { IDataDefaulFilter } from "..";
import { IRank } from "../../../../api/selectOptions/selectOptionsApi";
import '../styles.scss';
import { CaretDownOutlined, CaretRightOutlined } from "@ant-design/icons";
import { getReportVoucherConversionRateByCompanyUnit, IReportVoucherConversionRateByCompanyUnit } from "../../../../api/reportCustomer/reportCustomerApi";
import { useSelector } from "react-redux";
import { selectorInfoUser } from "../../../../store/slices/infoUserSlice";
import useToast from "../../../../hook/useToast";
import useLoading from "../../../../hook/useLoading";
import useDebounce from "../../../../hook/useDebounce";
import { useNavigate } from "react-router-dom";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface IVoucherUnitSummaryNode {
    id: string;
    parentId?: string;
    name: string;
    issuedCount?: number | null;     // Số phát hành
    convertedCount?: number | null;  // Số quy đổi
    convertedPercent?: number | null;// % quy đổi
}

interface FlatRow extends IVoucherUnitSummaryNode {
    _level: number;
    _hasChildren: boolean;
}

interface Props {
    dataFilter?: IDataDefaulFilter;
    dataRank?: IRank[];
    setTotal?: (e: number) => void;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_VOUCHER_UNIT_DATA: IVoucherUnitSummaryNode[] = [
    {
        id: '1',
        name: 'Cty TT/ĐL Phía Nam',
        issuedCount: null,
        convertedCount: null,
        convertedPercent: null,
    },
    {
        id: '1-1',
        parentId: '1',
        name: 'THACO AUTO TP.HCM',
        issuedCount: 10000,
        convertedCount: 500,
        convertedPercent: 5,
    },
    {
        id: '1-1-1',
        parentId: '1-1',
        name: 'SR Gò Vấp',
        issuedCount: 1000,
        convertedCount: 500,
        convertedPercent: 5,
    },
    {
        id: '1-1-2',
        parentId: '1-1',
        name: 'SR Bình Triệu',
        issuedCount: 1000,
        convertedCount: 500,
        convertedPercent: 5,
    },
    {
        id: '1-1-3',
        parentId: '1-1',
        name: 'SR Bình Dương',
        issuedCount: 1000,
        convertedCount: 500,
        convertedPercent: 5,
    },
    {
        id: '1-1-4',
        parentId: '1-1',
        name: 'SR Phú Nhuận',
        issuedCount: 1000,
        convertedCount: 500,
        convertedPercent: 5,
    },
    {
        id: '1-1-5',
        parentId: '1-1',
        name: 'SR Trường Chinh',
        issuedCount: 1000,
        convertedCount: 500,
        convertedPercent: 5,
    },
    {
        id: '1-1-6',
        parentId: '1-1',
        name: 'SR Phú Mỹ Hưng',
        issuedCount: 1000,
        convertedCount: 500,
        convertedPercent: 5,
    },
    {
        id: '2',
        name: 'Cty TT/ĐL Phía Bắc',
        issuedCount: null,
        convertedCount: null,
        convertedPercent: null,
    },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

type VoucherConversionRateNode = Omit<IReportVoucherConversionRateByCompanyUnit, 'children'> & {
    children?: VoucherConversionRateNode[];
};

const mapVoucherUnitSummaryNodes = (
    nodes: VoucherConversionRateNode[],
    parentId?: string,
): IVoucherUnitSummaryNode[] => nodes.flatMap((node) => {
    const id = String(node.id);
    return [
        {
            id,
            parentId,
            name: node.title,
            issuedCount: node.totalIssued,
            convertedCount: node.totalRedeemed,
            convertedPercent: node.rateRedeemed,
        },
        ...mapVoucherUnitSummaryNodes(node.children ?? [], id),
    ];
});

function buildFlatRows(
    nodes: IVoucherUnitSummaryNode[],
    expanded: Set<string>,
): FlatRow[] {
    const childrenMap = new Map<string | undefined, IVoucherUnitSummaryNode[]>();
    for (const n of nodes) {
        const key = n.parentId;
        if (!childrenMap.has(key)) childrenMap.set(key, []);
        childrenMap.get(key)!.push(n);
    }

    const result: FlatRow[] = [];

    function walk(parentId: string | undefined, level: number) {
        for (const node of childrenMap.get(parentId) ?? []) {
            const hasChildren = childrenMap.has(node.id);
            result.push({ ...node, _level: level, _hasChildren: hasChildren });
            if (hasChildren && expanded.has(node.id)) {
                walk(node.id, level + 1);
            }
        }
    }

    walk(undefined, 0);
    return result;
}

// ─── Component ────────────────────────────────────────────────────────────────

const ReportVoucherTableTotalMembers: React.FC<Props> = ({ setTotal, dataFilter }) => {

    const { unitTemp } = useSelector(selectorInfoUser)

    const pushToast = useToast();
    const pushLoading = useLoading()
    const navigate = useNavigate()

    // type: 1 - Số phát hành, 2 - Số quy đổi
    const goToCustomerList = (row: FlatRow, type: 1 | 2) => {
        navigate('/analysis-report/customers-card-list', {
            state: {
                firstPage: '/analysis-report/voucher-overview',
                dataFilterVoucher: dataFilter ?? {},
                unitIdVoucher: Number(row.id),
                typeCompanyUnit: type,
                title: `${type === 1 ? 'Số phát hành' : 'Số quy đổi'} - ${row.name}`,
            },
        });
    };

    const [expanded, setExpanded] = useState<Set<string>>(new Set(['1', '1-1']));

    const [data, setData] = useState<IReportVoucherConversionRateByCompanyUnit[]>()
    const [totalReport, setTotalReport] = useState<{
        totalIssued: number,
        totalRedeemed: number,
        rateRedeemed: number,
    }>()

    const gListReportVoucherConversionRateByCompanyUnit = useDebounce(async () => {
        pushLoading(true)
        const result = await getReportVoucherConversionRateByCompanyUnit(
            // pageSize, pageCurent,
            unitTemp, dataFilter?.unitIds, dataFilter?.campaignIds,
            // (YYYY-MM-DD)
            dataFilter?.dateStart, dataFilter?.dateEnd,
            // Loại hình voucher (0 - Công khai, 1 - Theo nhóm, 2 - Theo cá nhân)
            dataFilter?.type,
            dataFilter?.typeVoucherId
        )
        if (result.status) {
            setData(result.data)
            setTotalReport(result.summary)

        }
        pushLoading(false)
    }, 500)

    const nodes = useMemo(
        () => mapVoucherUnitSummaryNodes((data ?? []) as VoucherConversionRateNode[]),
        [data],
    );

    useEffect(() => {
        setTotal?.(nodes.length);
    }, [nodes, setTotal]);

    const toggle = (id: string) =>
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });

    const rowData = useMemo(() => buildFlatRows(nodes, expanded), [nodes, expanded]);

    const columnDefs = useMemo<ColDef<FlatRow>[]>(() => {
        return [
            {
                headerName: 'Công ty tỉnh thành',
                field: 'name',
                flex: 2,
                minWidth: 260,
                cellRenderer: (params: any) => {
                    const row: FlatRow = params.data;
                    const indent = row._level * 22;

                    return (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: indent,
                                height: '100%',
                                cursor: row._hasChildren ? 'pointer' : 'default',
                            }}
                            onClick={() => {
                                if (row._hasChildren) {
                                    toggle(row.id);
                                }
                            }}
                        >
                            {row._hasChildren ? (
                                <span
                                    style={{
                                        marginRight: 6,
                                        color: '#595959',
                                        flexShrink: 0,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {expanded.has(row.id) ? (
                                        <CaretDownOutlined style={{ fontSize: 12, color: '#595959' }} />
                                    ) : (
                                        <CaretRightOutlined style={{ fontSize: 12, color: '#595959' }} />
                                    )}
                                </span>
                            ) : (
                                <span style={{ marginRight: 6, width: 12, flexShrink: 0 }} />
                            )}

                            <span style={{ fontWeight: row._level === 0 ? 500 : 400, color: '#344054' }}>
                                {row.name}
                            </span>
                        </div>
                    );
                },
            },
            {
                headerName: 'Số phát hành',
                field: 'issuedCount',
                flex: 1,
                minWidth: 140,
                headerClass: 'ag-header-cell-center',
                valueFormatter: (p) => (p.value == null ? '--' : String(formatNumber(p.value))),
                onCellClicked: (p) => {
                    if (p.value != null && p.data) {
                        navigate('/marketing/vouchers-management', {
                            state: {
                                firstPage: '/analysis-report/voucher-overview',
                                dataFilterVoucher: dataFilter ?? {},
                                unitIdVoucher: Number(p.data.id),
                                // title: `${type === 1 ? 'Số phát hành' : 'Số quy đổi'} - ${p.data.name}`,
                            },
                        });
                    }
                },
                cellStyle: (p) => ({
                    textAlign: 'center',
                    color: p.value == null ? '#344054' : '#1677ff',
                    cursor: p.value == null ? 'default' : 'pointer',
                } as CellStyle),
            },
            {
                headerName: 'Số quy đổi',
                field: 'convertedCount',
                flex: 1,
                minWidth: 140,
                headerClass: 'ag-header-cell-center',
                valueFormatter: (p) => (p.value == null ? '--' : String(formatNumber(p.value))),
                onCellClicked: (p) => {
                    if (p.value != null && p.data) goToCustomerList(p.data, 2);
                },
                cellStyle: (p) => ({
                    textAlign: 'center',
                    color: p.value == null ? '#344054' : '#1677ff',
                    cursor: p.value == null ? 'default' : 'pointer',
                } as CellStyle),
            },
            {
                headerName: '% quy đổi',
                field: 'convertedPercent',
                flex: 1,
                minWidth: 140,
                headerClass: 'ag-header-cell-center',
                valueFormatter: (p) => (p.value == null ? '--' : String(formatNumber(p.value))),
                cellStyle: {
                    textAlign: 'center',
                    color: '#344054',
                } as CellStyle,
            },
        ];
    }, [expanded, dataFilter]);

    useEffect(() => {
        gListReportVoucherConversionRateByCompanyUnit()
    }, [dataFilter?.campaignIds, dataFilter?.dateEnd, dataFilter?.dateStart, dataFilter?.type, dataFilter?.typeVoucherId, dataFilter?.unitIds, unitTemp]);

    return (
        <div className="ag-theme-alpine custom-table-ag-v2" style={{ width: '100%' }}>
            <AgGridReact<FlatRow>
                domLayout="autoHeight"
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={{ resizable: true, sortable: false }}
                suppressMovableColumns
                suppressCellFocus
                rowHeight={44}
                getRowId={(p) => p.data.id}
                getRowStyle={(params) => ({
                    backgroundColor: params.rowIndex % 2 ? '#f7f9fc' : '#ffffff',
                })}
            />
        </div>
    );
};

export default ReportVoucherTableTotalMembers;
